import { cancel, fork, put, select, take, takeEvery } from 'redux-saga/effects';
import type { Task } from 'redux-saga';
import { push } from 'connected-react-router';
import {
    LOGIN_SUCCESS,
    LOGIN_FAILED,
    LOGIN_REQUESTED,
    PROFILE_REQUESTED,
    PROFILE_SUCCESS,
    PROFILE_FAILED,
    SPOT_LIST_REQUESTED,
    SPOT_LIST_FAILED,
    SPOT_LIST_SUCCESS,
    SET_AUTH_TOKEN,
    REPORTS_REQUESTED,
    REPORTS_FAILED,
    REPORTS_SUCCESS,
    PROMO_CODE_REPORTS_REQUESTED,
    PROMO_CODE_REPORTS_FAILED,
    PROMO_CODE_REPORTS_SUCCESS,
    SEND_VERIFICATION_CODE_REQUESTED,
    SEND_VERIFICATION_CODE_SUCCESS,
    SEND_VERIFICATION_CODE_FAILED,
    LOGOUT,
    SPOT_MULTIDAY_PRICING_SUCCESS,
    SPOT_MULTIDAY_PRICING_FAILED,
    SPOT_MULTIDAY_PRICING_REQUESTED,
    UPDATE_REPORTS_REQUESTED,
    UPDATE_REPORTS_SUCCESS,
    UPDATE_REPORTS_FAILED,
    LOGOUT_REQUESTED,
} from 'actions/constants';
import { tokenState, selectedOrganizationId, selectedSpotId } from 'selectors';
import ProfileService from 'services/ProfileService';
import SpotsService from 'services/SpotsService';
import ReportsService from 'services/ReportsService';
import OrganizationService from 'services/OrganizationService';
import { authClient } from '../clients';
import {
    ORGANIZATION_LIST_FAILED,
    ORGANIZATION_LIST_REQUESTED,
    ORGANIZATION_LIST_SUCCESS,
    ORGANIZATION_REQUESTED,
    ORGANIZATION_SPOTS_FAILED,
    ORGANIZATION_SPOTS_FETCH_STARTED,
    ORGANIZATION_SPOTS_REQUESTED,
    ORGANIZATION_SPOTS_SUCCESS,
    ORGANIZATION_SUCCESS,
} from 'contexts/OrganizationsContext/constants';
import Cookies from 'js-cookie';
import type { OrganizationListObj, SpotInterface } from 'types';
import * as amplitude from '@amplitude/analytics-browser';
import * as Sentry from '@sentry/react';
import env from 'env';
import { identifyPosthogUser, resetPosthogUser } from 'utils/posthog';

function assert(condition: boolean, failure_message = 'Invalid information provided') {
    if (!condition) {
        throw Error(failure_message);
    }
}

function* loginProfile({ payload }: any) {
    try {
        const { username, password } = payload;
        const token = yield ProfileService.getToken(username, password);
        assert(token, 'Invalid username or password');
        yield put({ type: LOGIN_SUCCESS, payload: { token: token } });
        yield put(push('/'));
    } catch (error) {
        yield put({ type: LOGIN_FAILED, message: error.toString() || 'Failed to login' });
    }
}

function* sendVerificationCode({ payload }: any) {
    try {
        const { username, phoneCode } = payload;
        const response = yield ProfileService.sendVerificationCode(username, phoneCode);
        assert(!response.error, response.error);
        yield put({ type: SEND_VERIFICATION_CODE_SUCCESS, payload: { username: username } });
    } catch (error) {
        yield put({
            type: SEND_VERIFICATION_CODE_FAILED,
            message: error.toString() || 'Failed to send a verification code',
        });
    }
}

function* getProfile() {
    yield put({ type: SET_AUTH_TOKEN });
    try {
        const token = yield select(tokenState);
        const profile = yield ProfileService.getProfileInfo(token);
        assert(profile.pk > 0);
        yield put({ type: PROFILE_SUCCESS, payload: { profile: profile } });
        const containsParkingOrg = profile.affiliations?.some(
            (affiliation: OrganizationListObj) => affiliation.type === 'parking'
        );
        if (containsParkingOrg || profile.superuser) {
            // Let login
            const name = profile.first_name + ' ' + profile.last_name;
            amplitude.setUserId(profile.email);
            const identify = new amplitude.Identify();
            const userType = profile.superuser ? 'superuser' : profile.affiliation?.role;
            identify.set('type', userType);
            amplitude.identify(identify);
            Sentry.setUser({
                name: name,
                username: profile.email,
            });
            identifyPosthogUser(profile);
            return;
        } else if (profile.is_renter) {
            window.location.assign(env.WEBPAY_DOMAIN);
            return;
        } else if (profile.org_type === 'towing') {
            yield put({ type: LOGOUT_REQUESTED });
        }

        yield put({ type: LOGOUT_REQUESTED });
    } catch {
        resetPosthogUser();
        yield put({ type: PROFILE_FAILED });
        const currentPath = (yield select((state: any) => state.router.location.pathname)) as string;
        if (currentPath !== '/login') {
            yield put(push('/login'));
        }
    }
}

function* getOrganization({ payload: { orgId } }: any) {
    const token = yield select(tokenState);
    const organization = yield OrganizationService.getOrganization(orgId, token);
    yield put({ type: ORGANIZATION_SUCCESS, payload: { organization: organization } });
}

function* getOrganizations() {
    const token = yield select(tokenState);
    try {
        const organizations = yield OrganizationService.getOrganizations(token);
        yield put({ type: ORGANIZATION_LIST_SUCCESS, payload: { organizations: organizations } });
    } catch (error) {
        yield put({ type: ORGANIZATION_LIST_FAILED, message: 'Cannot retrieve orgs' });
    }
}

function* getSpotList() {
    const token = yield select(tokenState);
    try {
        const spotList = yield SpotsService.getSpots(token);
        yield put({ type: SPOT_LIST_SUCCESS, payload: { spotList: spotList } });
    } catch (error) {
        yield put({ type: SPOT_LIST_FAILED, message: 'Cannot retrieve spots' });
    }
}

function* getSpots() {
    const token = yield select(tokenState);
    const organizationId = yield select(selectedOrganizationId);
    try {
        const spots = yield SpotsService.getSpotsInfo(organizationId, token);
        const spotState: { [key: string]: SpotInterface } = {};
        for (let i = 0; i < spots.length; i++) {
            spotState[spots[i].pk] = spots[i];
        }
        yield put({ type: ORGANIZATION_SPOTS_SUCCESS, payload: { spots: spotState } });
        return Date.now();
    } catch (error) {
        yield put({ type: ORGANIZATION_SPOTS_FAILED, message: error.toString() || 'Could not fetch spots' });
    }
}

export const SPOTS_FRESH_MS = 10000;

function* watchOrganizationSpots() {
    let task: Task | undefined;
    let taskOrgId: number | null = null;
    while (true) {
        const action: { type: string } = yield take([ORGANIZATION_REQUESTED, ORGANIZATION_SPOTS_REQUESTED]);
        const orgId: number | null = yield select(selectedOrganizationId);
        const completedAt = task?.result();
        const isFresh =
            orgId === taskOrgId &&
            (task?.isRunning() || (completedAt !== undefined && Date.now() - completedAt < SPOTS_FRESH_MS));
        if (action.type === ORGANIZATION_SPOTS_REQUESTED && isFresh) continue;
        if (task?.isRunning()) yield cancel(task);
        taskOrgId = orgId;
        yield put({ type: ORGANIZATION_SPOTS_FETCH_STARTED });
        task = yield fork(getSpots);
    }
}

function* getSpotMultidayPricing({ payload }: any) {
    const token = yield select(tokenState);
    const spotId = yield select(selectedSpotId);
    const { days } = payload as { days: number };
    try {
        const pricing = yield SpotsService.getMultidayPrices(spotId, days, token);
        yield put({ type: SPOT_MULTIDAY_PRICING_SUCCESS, payload: { pricing: pricing.result } });
    } catch (error) {
        yield put({ type: SPOT_MULTIDAY_PRICING_FAILED, message: error.message || 'Something went wrong' });
    }
}

function* getReports({ payload }: any) {
    const token = yield select(tokenState);
    const spotId = yield select(selectedSpotId);
    const { filters } = payload as { filters?: { date: { month: number; year: number } } };
    try {
        const reports = yield ReportsService.getReports(spotId, filters, token);
        yield put({ type: REPORTS_SUCCESS, payload: { reports: reports }, selectedSpotID: spotId });
    } catch (error) {
        yield put({ type: REPORTS_FAILED, message: error.toString() || 'Could not fetch reports' });
    }
}

function* updateReports() {
    const token = yield select(tokenState);
    const spotId = yield select(selectedSpotId);
    try {
        const reports = yield ReportsService.getReports(spotId, undefined, token);
        yield put({ type: UPDATE_REPORTS_SUCCESS, payload: { reports: reports }, selectedSpotID: spotId });
    } catch (error) {
        console.log('Failed to update reports');
        yield put({ type: UPDATE_REPORTS_FAILED });
    }
}

function* getPromoCodeReports({ payload }: any) {
    const token = yield select(tokenState);
    const spotId = yield select(selectedSpotId);
    const { filters } = payload;
    try {
        const promoCodeReports = yield ReportsService.getPromoCodeReports(spotId, filters, token);
        yield put({
            type: PROMO_CODE_REPORTS_SUCCESS,
            payload: { promoCodeReports: promoCodeReports },
            selectedSpotID: spotId,
        });
    } catch (error) {
        yield put({ type: PROMO_CODE_REPORTS_FAILED, message: error.toString() || 'Could not fetch reports' });
        console.log(error);
    }
}

function* logout() {
    yield authClient.logOut();
    resetPosthogUser();
    Cookies.remove('airgarage_auth', { path: '/', expires: 2000 });
    Cookies.remove('airgarage_auth', { domain: 'airgara.ge', path: '/', expires: 2000 });
    Cookies.remove('airgarage_auth', { domain: 'airgarage.com', path: '/', expires: 2000 });
    Cookies.remove('sessionid', { domain: 'airgarage.com', path: '/', expires: 2000 });
    yield put({ type: LOGOUT });
}

function* fetchSaga() {
    yield takeEvery(LOGIN_REQUESTED, loginProfile);
    yield takeEvery(SEND_VERIFICATION_CODE_REQUESTED, sendVerificationCode);
    yield takeEvery(PROFILE_REQUESTED, getProfile);
    yield takeEvery(ORGANIZATION_LIST_REQUESTED, getOrganizations);
    yield takeEvery(ORGANIZATION_REQUESTED, getOrganization);
    yield takeEvery(SPOT_LIST_REQUESTED, getSpotList);
    yield fork(watchOrganizationSpots);
    yield takeEvery(SPOT_MULTIDAY_PRICING_REQUESTED, getSpotMultidayPricing);
    yield takeEvery(REPORTS_REQUESTED, getReports);
    yield takeEvery(UPDATE_REPORTS_REQUESTED, updateReports);
    yield takeEvery(PROMO_CODE_REPORTS_REQUESTED, getPromoCodeReports);
    yield takeEvery(LOGOUT_REQUESTED, logout);
}

export default fetchSaga;
